import React, { useEffect, useState } from 'react';
import { Award, Search, ShieldCheck, ShieldOff } from 'lucide-react';
import { listCertificatesAdmin, revokeCertificateAdmin, restoreCertificateAdmin } from '../../lib/admin';
import type { Certificate } from '../../types/assess';
import { EmptyState, Panel, PanelHead, Stamp, fieldCls, labelCls, tdCls, thCls } from '../../components/lms/ui';
import { ListRowsSkeleton } from '../../components/lms/Skeletons';

const CertificatesAdmin: React.FC = () => {
  const [items, setItems] = useState<Certificate[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [revoking, setRevoking] = useState<Certificate | null>(null);
  const [restoring, setRestoring] = useState<Certificate | null>(null);
  const [reason, setReason] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      setItems(await listCertificatesAdmin().catch(() => []));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const revoke = async () => {
    if (!revoking) return;
    if (!reason.trim()) {
      setError('A revocation needs a reason on record.');
      return;
    }
    setBusy(revoking.id);
    setError('');
    try {
      await revokeCertificateAdmin(revoking.id, reason.trim());
      setRevoking(null);
      setReason('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not revoke the certificate.');
    } finally {
      setBusy('');
    }
  };

  const restore = async () => {
    if (!restoring) return;
    setBusy(restoring.id);
    setError('');
    try {
      await restoreCertificateAdmin(restoring.id);
      setRestoring(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not restore the certificate.');
    } finally {
      setBusy('');
    }
  };

  const filtered = items.filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [c.student_name, c.course_title, c.code].some((v) => (v || '').toLowerCase().includes(q));
  });

  return (
    <div className="mt-6 space-y-4">
      {error && (
        <p className="border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger" role="alert">{error}</p>
      )}

      <Panel className="overflow-hidden">
        <PanelHead
          title="Certificate register"
          action={
            <label className="relative block w-full sm:w-[280px]">
              <span className="sr-only">Search certificates</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-2" strokeWidth={1.5} />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search student, programme or code"
                className={`${fieldCls} !py-1.5 !text-[12px] pl-8`}
              />
            </label>
          }
        />

        {loading ? (
          <ListRowsSkeleton rows={4} label="Loading certificates" />
        ) : filtered.length === 0 ? (
          <EmptyState
            title={query ? 'No record matches that search' : 'No certificates issued yet'}
            body={query ? 'Check the spelling of the student, programme or verification code.' : 'A certificate enters the register when a student completes 100% of a programme.'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead className="border-b border-rule bg-paper/60">
                <tr>
                  <th className={thCls}>Student</th>
                  <th className={thCls}>Programme</th>
                  <th className={thCls}>Code</th>
                  <th className={thCls}>Issued</th>
                  <th className={thCls}>Status</th>
                  <th className={`${thCls} text-right`}>Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule">
                {filtered.map((c) => (
                  <React.Fragment key={c.id}>
                    <tr className="align-top hover:bg-paper/40">
                      <td className={tdCls}>
                        <p className="font-medium text-ink">{c.student_name || 'Unnamed student'}</p>
                        <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.08em] text-ink-2">{c.user_id.slice(0, 8)}…</p>
                      </td>
                      <td className={tdCls}>
                        <p className="max-w-[30ch] leading-5">{c.course_title || c.course_id}</p>
                      </td>
                      <td className={`${tdCls} font-mono text-[11px] uppercase tracking-[0.08em] whitespace-nowrap`}>{c.code}</td>
                      <td className={`${tdCls} whitespace-nowrap font-mono text-[10.5px] text-ink-2`}>
                        {new Date(c.issued_at).toLocaleDateString('en-NG')}
                      </td>
                      <td className={tdCls}>
                        <Stamp tone={c.revoked ? 'held' : 'cleared'}>{c.revoked ? 'Revoked' : 'Valid'}</Stamp>
                        {c.revoked && c.revoked_reason && (
                          <p className="mt-1 max-w-[26ch] text-[11px] leading-4 text-ink-2">{c.revoked_reason}</p>
                        )}
                      </td>
                      <td className={`${tdCls} text-right`}>
                        {c.revoked ? (
                          <button
                            type="button"
                            onClick={() => { setRestoring(c); setRevoking(null); setError(''); }}
                            disabled={busy === c.id}
                            className="lms-btn-secondary !px-2.5 !py-1.5 !text-[11px]"
                          >
                            <ShieldCheck className="h-3 w-3" strokeWidth={1.5} /> Restore
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => { setRevoking(c); setRestoring(null); setReason(''); setError(''); }}
                            disabled={busy === c.id}
                            className="lms-btn-danger !px-2.5 !py-1.5 !text-[11px]"
                          >
                            <ShieldOff className="h-3 w-3" strokeWidth={1.5} /> Revoke
                          </button>
                        )}
                      </td>
                    </tr>

                    {revoking?.id === c.id && (
                      <tr className="bg-paper">
                        <td colSpan={6} className="px-5 py-4">
                          <form
                            onSubmit={(e) => { e.preventDefault(); void revoke(); }}
                            className="flex flex-col gap-3 sm:flex-row sm:items-end"
                          >
                            <div className="min-w-0 flex-1">
                              <label htmlFor={`revoke-reason-${c.id}`} className={labelCls}>Reason on record</label>
                              <input
                                id={`revoke-reason-${c.id}`}
                                autoFocus
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Why public verification should fail for this certificate"
                                className={fieldCls}
                              />
                              <p className="mt-1.5 text-[11px] leading-4 text-ink-2">
                                The reason is shown on the public register and the student's certificate page.
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button type="submit" disabled={busy === c.id} className="lms-btn bg-danger text-white hover:bg-danger/90 !px-3.5 !py-2 !text-[12px]">
                                {busy === c.id ? 'Revoking…' : 'Confirm revoke'}
                              </button>
                              <button type="button" onClick={() => { setRevoking(null); setReason(''); }} className="lms-btn-quiet !px-3 !py-2 !text-[12px]">
                                Cancel
                              </button>
                            </div>
                          </form>
                        </td>
                      </tr>
                    )}

                    {restoring?.id === c.id && (
                      <tr className="bg-paper">
                        <td colSpan={6} className="px-5 py-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <p className="text-[12.5px] font-medium text-ink">Restore {c.code} to the public register?</p>
                              <p className="mt-0.5 text-[11.5px] leading-5 text-ink-2">
                                Verification will succeed again and the revocation note will be cleared from the record.
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button type="button" onClick={() => void restore()} disabled={busy === c.id} className="lms-btn-primary !px-3.5 !py-2 !text-[12px]">
                                {busy === c.id ? 'Restoring…' : 'Confirm restore'}
                              </button>
                              <button type="button" onClick={() => setRestoring(null)} className="lms-btn-quiet !px-3 !py-2 !text-[12px]">
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <div className="flex items-center gap-2 px-1">
        <Award className="h-3.5 w-3.5 text-ink-2" strokeWidth={1.5} />
        <p className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">
          Public verification lives at lsem.org.ng/verify
        </p>
      </div>
    </div>
  );
};

export default CertificatesAdmin;
